const EMAIL_ELEMENT_STYLES: Partial<Record<keyof HTMLElementTagNameMap, string>> = {
  p: 'margin:0 0 10px;line-height:1.55;',
  h1: 'margin:18px 0 8px;font-size:24px;line-height:1.25;font-weight:700;',
  h2: 'margin:16px 0 8px;font-size:20px;line-height:1.3;font-weight:700;',
  h3: 'margin:14px 0 6px;font-size:17px;line-height:1.35;font-weight:700;',
  h4: 'margin:12px 0 6px;font-size:15px;line-height:1.4;font-weight:700;',
  ul: 'margin:8px 0 12px;padding-left:24px;',
  ol: 'margin:8px 0 12px;padding-left:24px;',
  li: 'margin:3px 0;line-height:1.5;',
  blockquote: 'margin:12px 0;padding:4px 0 4px 12px;border-left:3px solid #d1d5db;color:#4b5563;',
  pre: 'margin:12px 0;padding:12px;overflow:auto;border:1px solid #d1d5db;border-radius:8px;background:#f6f7f8;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace;font-size:12px;line-height:1.5;white-space:pre-wrap;',
  table: 'width:100%;margin:12px 0;border-collapse:collapse;font-size:13px;',
  th: 'padding:6px 8px;border:1px solid #d1d5db;background:#f3f4f6;text-align:left;font-weight:600;',
  td: 'padding:6px 8px;border:1px solid #d1d5db;text-align:left;',
  a: 'color:#9a5b08;text-decoration:underline;',
  hr: 'margin:16px 0;border:0;border-top:1px solid #d1d5db;',
};

function toSafeAbsoluteUrl(value: string): string | null {
  try {
    const url = new URL(value, window.location.origin);
    if (!['http:', 'https:', 'mailto:'].includes(url.protocol)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function createRichEmailHtml(root: HTMLElement): string {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('[data-copy-control]').forEach((element) => element.remove());

  clone.querySelectorAll('img').forEach((image) => {
    const href = toSafeAbsoluteUrl(image.getAttribute('src') || '');
    const replacement = document.createElement(href ? 'a' : 'span');
    replacement.textContent = image.getAttribute('alt') || 'View image';
    if (href) replacement.setAttribute('href', href);
    image.replaceWith(replacement);
  });

  clone.querySelectorAll<HTMLElement>('*').forEach((element) => {
    const tagName = element.tagName.toLowerCase() as keyof HTMLElementTagNameMap;
    const href = tagName === 'a' ? toSafeAbsoluteUrl(element.getAttribute('href') || '') : null;

    for (const attribute of element.getAttributeNames()) element.removeAttribute(attribute);
    if (href) element.setAttribute('href', href);

    if (tagName === 'code') {
      const inCodeBlock = element.parentElement?.tagName.toLowerCase() === 'pre';
      element.setAttribute(
        'style',
        inCodeBlock
          ? 'font-family:inherit;'
          : 'padding:1px 4px;border-radius:4px;background:#f3f4f6;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace;font-size:0.9em;'
      );
      return;
    }

    const style = EMAIL_ELEMENT_STYLES[tagName];
    if (style) element.setAttribute('style', style);
  });

  return `<div style="color:#171717;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;">${clone.innerHTML}</div>`;
}

export async function copyRichText(plainText: string, html: string): Promise<void> {
  try {
    if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);
      return;
    }
  } catch {
    // Some browsers expose ClipboardItem without allowing rich writes. Fall back to plain text.
  }

  await navigator.clipboard.writeText(plainText);
}

export async function copyPlainText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}
